/**
 * Assessment Status Indicators — helper utilities
 * Spec: assessment-status-indicators-spec.md
 *
 * Visual constants from spec:
 *   d = 8 px              — indicator dot diameter
 *   d = 30 px             — reserved / phenomenon dot reference
 *   >= 1 px               — minimum gap between phenomenon dot and indicator
 *   >= 2 px               — minimum gap between indicator dots
 *   ~27.5°                — angular spacing between indicator dots
 *   line-height (0.9)     — phenomena label line-height
 *   text width -5%        — reduce phenomenon text label width by 5%
 */

export const INDICATOR_SIZE = 8          // d = 8px
export const INDICATOR_RADIUS = 4        // = INDICATOR_SIZE / 2
export const INDICATOR_ANGLE_DEG = 27.5  // ~27.5° spacing
export const INDICATOR_GAP = 2           // >= 2px gap from dot edge
export const TEXT_WIDTH_REDUCTION = 0.95 // 5% reduction → multiply labelWidth by this

/**
 * Returns the display state for a single collaboration tool indicator on a phenomenon.
 *
 * @param {object} params
 * @param {boolean} params.toolEnabled      Is this collaboration tool globally enabled?
 * @param {boolean} params.hasAnyActivity   Has any user performed activity on this phenomenon?
 * @param {boolean} params.hasOwnActivity   Has the current user personally participated?
 * @returns {'hidden'|'idle-outline'|'idle-filled'|'active-filled'}
 */
export function getIndicatorState({ toolEnabled, hasAnyActivity, hasOwnActivity }) {
  if (!toolEnabled) return 'hidden'
  if (hasOwnActivity) return 'active-filled'
  if (!hasAnyActivity) return 'idle-outline'
  return 'idle-filled'
}

/**
 * Computes SVG (x, y) positions for n indicator dots placed angularly on the
 * right side of a phenomenon dot.
 *
 * @param {number} cx          Centre x of phenomenon dot
 * @param {number} cy          Centre y of phenomenon dot
 * @param {number} dotRadius   Radius of the phenomenon dot (phenomenaSize / 2)
 * @param {number} count       Number of indicators to position (1–3)
 * @returns {{ x: number, y: number }[]}
 */
export function getIndicatorPositions(cx, cy, dotRadius, count, indicatorR = INDICATOR_RADIUS, gap = INDICATOR_GAP) {
  const R = dotRadius + gap + indicatorR  // distance from phenomenon centre to indicator centre
  const angleStep = INDICATOR_ANGLE_DEG * (Math.PI / 180)
  const totalAngle = (count - 1) * angleStep
  const startAngle = -totalAngle / 2  // centre the group at 0° (3 o'clock)

  return Array.from({ length: count }, (_, i) => {
    const angle = startAngle + i * angleStep
    return {
      x: cx + R * Math.cos(angle),
      y: cy + R * Math.sin(angle)
    }
  })
}

/**
 * Returns the localStorage key used to persist the showAssessmentStatus preference
 * for a specific user.
 *
 * @param {string|number} userId
 * @param {string|number} groupId
 * @param {string|number} radarId
 * @returns {string}
 */
export function getAssessmentStatusStorageKey(userId, groupId, radarId) {
  return `assessment-status-${userId}-${groupId}-${radarId}`
}
